'use client';

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCheck,
  faChevronRight,
  faFolderBookmark,
  faFolderOpen,
} from '@fortawesome/pro-solid-svg-icons';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { useGetAccountFolders } from 'src/hooks/accountFolders';

interface FilterSet {
  id: string;
  label: string;
  type: string;
  description?: string;
  scope?: 'COMPANY' | 'PERSONAL';
}

interface FolderData {
  id: number | string;
  label: string;
  scope: 'COMPANY' | 'PERSONAL';
  items: FilterSet[];
}

interface SectionData {
  folders: FolderData[];
  ungroupedItems: FilterSet[];
}

interface StrategyFilterSetDropdownProps {
  selectedFilterSetId?: string;
  onFilterSetSelection: (filterSet: FilterSet | null) => void;
}

const StrategyFilterSetDropdown: React.FC<StrategyFilterSetDropdownProps> = ({
  selectedFilterSetId,
  onFilterSetSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const [expandedSection, setExpandedSection] = useState<
    'company' | 'personal' | null
  >('company');
  const [expandedFolder, setExpandedFolder] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Fetch all filter sets
  const { data: filterSetsData, isLoading: isFilterSetsLoading } =
    useGetFilterSetsQuery({
      serializer: 'detailed',
    });

  // Fetch account folders
  const { data: foldersData, isLoading: isFoldersLoading } =
    useGetAccountFolders();

  const isLoading = isFilterSetsLoading || isFoldersLoading;

  // Organize data by scope with folders
  const sections = useMemo((): {
    company: SectionData;
    personal: SectionData;
  } => {
    const empty = {
      company: { folders: [], ungroupedItems: [] } as SectionData,
      personal: { folders: [], ungroupedItems: [] } as SectionData,
    };

    if (!filterSetsData?.results) return empty;

    const searchLower = searchTerm.toLowerCase();

    // Create lookup map for filter sets
    const filterSetsMap = new Map<number, any>();
    filterSetsData.results.forEach((item: any) => {
      filterSetsMap.set(item.id, item);
    });

    // Get only FILTER type items
    const filterTypeItems = filterSetsData.results.filter(
      (item: any) => item.type === 'FILTER'
    );

    // Track items in folders
    const itemsInFolders = new Set<number>();
    if (foldersData?.results) {
      foldersData.results.forEach((folder: any) => {
        folder.filter_sets?.forEach((item: any) => {
          itemsInFolders.add(item.id);
        });
      });
    }

    const result = {
      company: { folders: [], ungroupedItems: [] } as SectionData,
      personal: { folders: [], ungroupedItems: [] } as SectionData,
    };

    // Organize folders by scope
    if (foldersData?.results) {
      foldersData.results.forEach((folder: any) => {
        const scopeKey: 'company' | 'personal' =
          folder.scope === 'COMPANY' ? 'company' : 'personal';

        // Get folder items enriched with full data, filtered to FILTER type
        const folderItems = (folder.filter_sets || [])
          .map((item: any) => filterSetsMap.get(item.id) || item)
          .filter((item: any) => {
            if (item.type !== 'FILTER') return false;
            if (searchTerm && !item.label?.toLowerCase().includes(searchLower))
              return false;
            return true;
          })
          .sort((a: any, b: any) =>
            (a.label || '').localeCompare(b.label || '')
          );

        if (folderItems.length > 0 || !searchTerm) {
          result[scopeKey].folders.push({
            id: folder.id,
            label: folder.label,
            scope: folder.scope,
            items: folderItems,
          });
        }
      });
    }

    // Sort folders alphabetically
    result.company.folders.sort((a, b) => a.label.localeCompare(b.label));
    result.personal.folders.sort((a, b) => a.label.localeCompare(b.label));

    // Add ungrouped FILTER items (not in any folder)
    filterTypeItems.forEach((item: any) => {
      if (itemsInFolders.has(item.id)) return;
      if (searchTerm && !item.label?.toLowerCase().includes(searchLower))
        return;

      const scopeKey: 'company' | 'personal' =
        item.scope === 'COMPANY' ? 'company' : 'personal';
      result[scopeKey].ungroupedItems.push(item);
    });

    // Sort ungrouped items
    result.company.ungroupedItems.sort((a, b) =>
      (a.label || '').localeCompare(b.label || '')
    );
    result.personal.ungroupedItems.sort((a, b) =>
      (a.label || '').localeCompare(b.label || '')
    );

    return result;
  }, [filterSetsData, foldersData, searchTerm]);

  // Get selected filter set
  const selectedFilterSet = useMemo(() => {
    if (!selectedFilterSetId) return undefined;
    const allItems = [
      ...sections.company.folders.flatMap((f) => f.items),
      ...sections.company.ungroupedItems,
      ...sections.personal.folders.flatMap((f) => f.items),
      ...sections.personal.ungroupedItems,
    ];
    return allItems.find(
      (item) => Number(item.id) === Number(selectedFilterSetId)
    );
  }, [sections, selectedFilterSetId]);

  const handleFilterSetSelect = (filterSet: FilterSet | null) => {
    onFilterSetSelection(filterSet);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleSection = useCallback((sectionKey: 'company' | 'personal') => {
    setExpandedSection((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolder((prev) => (prev === folderId ? null : folderId));
  }, []);

  const calculateDropdownPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = 300;

    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  const hasCompany =
    sections.company.folders.length > 0 ||
    sections.company.ungroupedItems.length > 0;
  const hasPersonal =
    sections.personal.folders.length > 0 ||
    sections.personal.ungroupedItems.length > 0;

  const renderItem = (item: FilterSet) => {
    const isSelected =
      selectedFilterSetId != null &&
      Number(item.id) === Number(selectedFilterSetId);
    return (
      <div
        key={item.id}
        onClick={() => handleFilterSetSelect(item)}
        className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
        data-testid={`filter-set-option-${item.id}`}
      >
        <span className="text-sm text-slate-900">{item.label}</span>
        {isSelected && (
          <Icon icon={faCheck} className="w-4 h-4 text-sky-600 ml-auto" />
        )}
      </div>
    );
  };

  const renderFolder = (folder: FolderData) => {
    if (folder.items.length === 0) return null;
    const isFolderExpanded = expandedFolder === folder.id?.toString();

    return (
      <div key={folder.id} className="w-full">
        <div
          className="w-full h-9 px-4 rounded-lg inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100"
          onClick={() => toggleFolder(folder.id.toString())}
          data-testid={`filter-set-folder-${folder.id}`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <Icon
              icon={isFolderExpanded ? faFolderOpen : faFolderBookmark}
              className="w-3.5 h-3.5 text-neutral-600"
              aria-hidden="true"
            />
          </div>
          <span className="text-sm font-medium text-neutral-700">
            {folder.label}
          </span>
        </div>
        {isFolderExpanded && (
          <div className="pl-2">{folder.items.map(renderItem)}</div>
        )}
      </div>
    );
  };

  const renderSection = (
    title: string,
    sectionKey: 'company' | 'personal',
    sectionData: SectionData,
    hasItems: boolean
  ) => {
    if (!hasItems) return null;

    const isExpanded = expandedSection === sectionKey;

    return (
      <div key={sectionKey}>
        <button
          type="button"
          className="w-full h-9 px-3 bg-neutral-50 inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100 border-b border-neutral-200"
          onClick={() => toggleSection(sectionKey)}
          aria-expanded={isExpanded}
          data-testid={`filter-set-${sectionKey}-section`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <Icon
              icon={faChevronRight}
              className={`w-3 h-3 text-neutral-600 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
              aria-hidden="true"
            />
          </div>
          <span className="text-sm font-semibold text-neutral-700">
            {title}
          </span>
        </button>

        {isExpanded && (
          <div className="overflow-y-auto max-h-80">
            {sectionData.folders.map(renderFolder)}
            {sectionData.ungroupedItems.map(renderItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-1">
        <label className="text-primary text-sm font-semibold leading-tight">
          Filter Set{' '}
          <span className="text-neutral-500 font-normal">(Optional)</span>
        </label>
      </div>

      {/* Dropdown Trigger */}
      <div className="relative">
        <button
          type="button"
          ref={triggerRef}
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="w-full px-4 py-3 border border-slate-200
           rounded-full focus:outline-none bg-white shadow-sm hover:shadow-md transition-all duration-200 text-left flex justify-between items-center min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="filter-set-dropdown-trigger"
        >
          <div className="flex-1 flex items-center gap-2 overflow-hidden">
            {!selectedFilterSet ? (
              <span className="text-slate-500">
                {isLoading ? 'Loading...' : 'Select a filter set...'}
              </span>
            ) : (
              <span className="text-slate-900">{selectedFilterSet.label}</span>
            )}
          </div>
          <Icon
            icon={faCaretDown}
            className={`w-4 h-4 text-slate-500 transition-all duration-300 ${
              isOpen ? '-rotate-90' : ''
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && !isLoading && (
          <div
            className={`absolute left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg z-50 ${
              dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-slate-200">
              <input
                autoFocus
                type="text"
                placeholder="Search filter sets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-full focus:outline-none text-sm"
                data-testid="filter-set-search-input"
              />
            </div>

            {/* Filter Sets List */}
            <div>
              {/* Clear selection option */}
              {selectedFilterSetId && (hasCompany || hasPersonal) && (
                <div
                  onClick={() => handleFilterSetSelect(null)}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100"
                  data-testid="filter-set-clear-option"
                >
                  <span className="text-sm text-slate-500">
                    Clear selection
                  </span>
                </div>
              )}

              {!hasCompany && !hasPersonal ? (
                <div className="p-3 text-sm text-slate-500">
                  No filter sets found
                </div>
              ) : (
                <>
                  {renderSection(
                    'Company',
                    'company',
                    sections.company,
                    hasCompany
                  )}
                  {renderSection(
                    'Personal',
                    'personal',
                    sections.personal,
                    hasPersonal
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Helper text - positioned outside relative container */}
      <span className="text-xs text-neutral-500">
        Please Note: If a payer type is included on a filter set, that filter
        will be ignored for the results of the Fresh Faces list for now, but
        other filters on that filter set will be applied.
      </span>

      {/* Click outside to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        />
      )}
    </div>
  );
};

export default StrategyFilterSetDropdown;
